import { FC } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Chip, Typography } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { DataGrid } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { UserbotBatches } from "../interfaces"
import { useAccountBatchesQuery } from "../store"
import { UserbotPage } from "./userbotPage"
import { BATCH_JOB_STATUS_META } from "../constants/batchStatusMeta"
import { describeBatchTarget } from "../components/batches/batchTarget"
import { useTemplateName } from "../components/batches/resolveTemplateName"

const TemplateNameCell: FC<{ templateId: string }> = ({ templateId }) => {
  const { name, resolved } = useTemplateName(templateId)
  return (
    <Typography
      variant="body2"
      sx={resolved ? undefined : { fontFamily: "monospace" }}
    >
      {name}
    </Typography>
  )
}

export const BatchesPage: FC = () => {
  const navigate = useNavigate()
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)

  const { data, isFetching, isError, error } = useAccountBatchesQuery({
    limit: pageSize,
    offset: page * pageSize,
  })

  const columns: GridColDef<UserbotBatches.AccountBatchJob>[] = [
    {
      field: "createdAt",
      headerName: "Создан",
      flex: 2,
      valueGetter: ({ row }) => formatDateAndTime(row.createdAt),
    },
    {
      field: "templateId",
      headerName: "Действие",
      flex: 2,
      renderCell: ({ row }) =>
        row.templateId ? (
          <TemplateNameCell templateId={row.templateId} />
        ) : (
          <Typography variant="body2">
            {row.bodySnapshot?.name ?? "Группа цепочек"}
          </Typography>
        ),
    },
    {
      field: "targetSnapshot",
      headerName: "Цель",
      flex: 1.5,
      valueGetter: ({ row }) =>
        describeBatchTarget(
          row.targetSnapshot as UserbotBatches.AccountBatchTarget,
        ),
    },
    {
      field: "counters",
      headerName: "Итог (успех/ошибка/пропуск/отмена)",
      flex: 2,
      sortable: false,
      valueGetter: ({ row }) =>
        `${row.counters.succeeded}/${row.counters.failed}/${row.counters.skipped}/${row.counters.cancelled} из ${row.counters.total}`,
    },
    {
      field: "status",
      headerName: "Статус",
      flex: 1,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={BATCH_JOB_STATUS_META[row.status].label}
          color={BATCH_JOB_STATUS_META[row.status].color}
        />
      ),
    },
  ]

  return (
    <UserbotPage title="Запуски по аккаунтам">
      {isError && (
        <Typography color="error" mb={1}>
          {getApiErrorMessage(error)}
        </Typography>
      )}
      <Box height="70vh">
        <DataGrid<UserbotBatches.AccountBatchJob>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          onRowClick={({ row }) => navigate(`/userbot/batches/${row.id}`)}
        />
      </Box>
    </UserbotPage>
  )
}
